import { SKIP_WAITING_MESSAGE, clearUpdatePending } from "./pendingUpdate.ts";

/** How long to wait for a newly-found worker to finish installing. */
export const APP_UPDATE_WAIT_MS = 8000;

/**
 * The least a worker told to take over is given to do it, whatever is left
 * of the budget: a hand-off cut to nothing reloads onto the old build with
 * the new one still waiting (client 2026-10-09).
 */
const HANDOFF_MIN_MS = 2500;

export type AppUpdateOutcome =
  /** A new version was fetched and is ready; the reload will land on it. */
  | "updated"
  /** Already on the newest version, or the browser has no service worker. */
  | "up-to-date"
  /** Could not check (offline, registration missing, probe failed). */
  | "unavailable";

/**
 * Resolve `promise`, or give up with `onTimeout` once `ms` have passed. The
 * underlying work is not cancellable — it is simply no longer waited on.
 */
async function withDeadline<T>(promise: Promise<T>, ms: number, onTimeout: T): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise.then((value) => value),
      new Promise<T>((resolve) => {
        timer = setTimeout(() => resolve(onTimeout), ms);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Ask a waiting worker to take over (the service worker listens for
 * SKIP_WAITING) and wait, bounded, for `controllerchange` — the moment it
 * actually controls this page. Resolves either way: a worker that never
 * answers must not strand the coach on "Checking…".
 */
export async function activateWaitingWorker(
  worker: ServiceWorker,
  ms: number
): Promise<boolean> {
  const container = navigator.serviceWorker as
    | (ServiceWorkerContainer & { addEventListener?: unknown })
    | undefined;
  // Listen BEFORE asking: a worker can take over within the same tick, and a
  // listener added after the message would miss it.
  const canListen = typeof container?.addEventListener === "function";
  let resolveHandoff: (handedOff: boolean) => void = () => {};
  const handoff = new Promise<boolean>((resolve) => {
    resolveHandoff = resolve;
  });
  const onChange = () => resolveHandoff(true);
  if (canListen) container.addEventListener("controllerchange", onChange);
  try {
    worker.postMessage?.(SKIP_WAITING_MESSAGE);
  } catch {
    if (canListen) container.removeEventListener("controllerchange", onChange);
    return false;
  }
  // Nowhere to hear the hand-off from (no container events): assume it.
  // Otherwise resolve TRUE only when the new worker actually took control; a
  // worker that never answers resolves false at the deadline, and the caller
  // must not then reload as if it had — that reloads onto the OLD build.
  if (!canListen) return true;
  const timer = setTimeout(() => resolveHandoff(false), ms);
  try {
    return await handoff;
  } finally {
    clearTimeout(timer);
    container.removeEventListener("controllerchange", onChange);
  }
}

/**
 * Pull a new app version, if one has been published (in-game parity, client
 * 2026-10-07).
 *
 * "Check For Updates" used to be a bare `window.location.reload()`. A reload
 * re-runs the SAME cached bundle: the browser only swaps in a new build once
 * the service worker has fetched and installed it, and installed PWAs rarely
 * re-check the worker on their own. So a coach on a stale build could press
 * the button all day and stay on it.
 *
 * This asks the worker to re-check NOW, waits for the install to finish, and
 * hands the page to it, so the reload that follows actually lands on the new
 * version.
 *
 * Never throws, and never blocks longer than `waitMs` IN TOTAL — the probe
 * and the install wait share one deadline: a failed or slow check must still
 * let the caller reload — except that a worker told to take over always gets
 * HANDOFF_MIN_MS to do it.
 */
export async function checkForAppUpdate(
  waitMs: number = APP_UPDATE_WAIT_MS
): Promise<AppUpdateOutcome> {
  try {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
      return "unavailable";
    }
    // The deadline starts HERE: getRegistration() settles off the
    // service-worker job queue and has no timeout of its own either.
    const deadline = Date.now() + waitMs;
    const registration = await withDeadline(
      navigator.serviceWorker.getRegistration(),
      waitMs,
      undefined
    );
    if (!registration) return "unavailable";

    // A worker already WAITING (found earlier: the red dot) is complete and
    // needs no network, so it is taken at once, before any probe: on a hung
    // stadium link the probe would eat the time the hand-off needs, and one
    // that found an even newer build would start it downloading as this one
    // takes over (that one installs later, and prompts in turn).
    if (registration.waiting) {
      const handedOff = await activateWaitingWorker(
        registration.waiting,
        Math.max(deadline - Date.now(), HANDOFF_MIN_MS)
      );
      if (!handedOff) return "unavailable";
      clearUpdatePending();
      return "updated";
    }

    // MUST be bounded: update() is a network fetch of the worker script with
    // no timeout of its own — on a half-open stadium link it can stall for
    // minutes, leaving the coach on a disabled "Checking..." button.
    const probed = await withDeadline(
      registration
        .update()
        .catch(() => false)
        .then((v) => v !== false),
      Math.max(deadline - Date.now(), 0),
      false
    );

    // An update the probe FOUND may still be installing; reloading mid-install
    // lands on the old bundle, so wait for it (it may also have finished).
    const worker: ServiceWorker | null = registration.installing ?? registration.waiting;
    if (!worker) return probed ? "up-to-date" : "unavailable";

    const pendingWorker: ServiceWorker = worker;
    const remaining = Math.max(deadline - Date.now(), 0);
    const installed = await new Promise<boolean>((resolve) => {
      // settle(), not resolve(): the timeout path must also drop the listener.
      const timer = setTimeout(() => settle(false), remaining);
      const settle = (value: boolean) => {
        clearTimeout(timer);
        pendingWorker.removeEventListener("statechange", onStateChange);
        resolve(value);
      };
      const onStateChange = () => {
        // "installed" counts as ready: a worker parked in `waiting` is already
        // installed and fires no further statechange while this page holds a
        // controller.
        if (pendingWorker.state === "installed" || pendingWorker.state === "activated") {
          settle(true);
        }
        // A worker that errors out is not going to arrive.
        if (pendingWorker.state === "redundant") settle(false);
      };
      pendingWorker.addEventListener("statechange", onStateChange);
      onStateChange();
    });

    if (!installed) return "unavailable";
    // An installed worker WAITS until told to take over. Tell it, and hold
    // (bounded) for the hand-off, so the reload lands on the new build.
    const handedOff = await activateWaitingWorker(
      pendingWorker,
      Math.max(deadline - Date.now(), HANDOFF_MIN_MS)
    );
    if (!handedOff) return "unavailable";
    clearUpdatePending();
    return "updated";
  } catch {
    // Offline, blocked, or an unsupported browser — the caller still reloads.
    return "unavailable";
  }
}

/**
 * Take the update that is already installed and WAITING — the "Update now"
 * of the prompt (UpdateAvailableModal). Unlike checkForAppUpdate this does no
 * probe: the worker is here, so tell it to take over, then reload onto it.
 *
 * Resolves to false when nothing is waiting (the coach can still use Check
 * For Updates, which probes). Never throws.
 */
export async function takePendingUpdate(
  waitMs: number = APP_UPDATE_WAIT_MS,
  reload: () => void = () => window.location.reload()
): Promise<boolean> {
  try {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return false;
    // One deadline for the lookup and the hand-off together.
    const deadline = Date.now() + waitMs;
    const registration = await withDeadline(
      navigator.serviceWorker.getRegistration(),
      waitMs,
      undefined
    );
    const worker = registration?.waiting;
    if (!worker) return false;
    const handedOff = await activateWaitingWorker(worker, Math.max(deadline - Date.now(), 0));
    if (!handedOff) return false;
    clearUpdatePending();
    reload();
    return true;
  } catch {
    return false;
  }
}

/**
 * For the "update required" recovery screens (a role that failed to load, a
 * stale chunk): reloading the page does NOT activate a waiting worker — the
 * page is still its client — so it would come back on the same old build.
 * Take the waiting update when there is one (`go` runs on the new build),
 * else just `go` as before.
 */
export function takePendingUpdateOr(go: () => void): void {
  void takePendingUpdate(APP_UPDATE_WAIT_MS, go).then((taken) => {
    if (!taken) go();
  });
}
